import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Alert, IconButton, Tooltip, Chip, Collapse, Paper, Stack, ToggleButton, ToggleButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField, Checkbox,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
  HelpOutline as HelpIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { useKlantAfkortingen } from '../../utils/klantAfkortingen';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';
import AfgerondSignaal from '../../ui-components/AfgerondSignaal';
import { computeDayLoad, weekAveragePct, effectiveTaskHours, taskProjectLabel, DEFAULT_CAPACITY_RULES } from '../../utils/capacityCalc';
import CapaciteitsRegels from '../../ui-components/CapaciteitsRegels';
import { useCapacityRules } from '../../utils/useCapacityRules';
import WeekSelector from '../../ui-components/WeekSelector';
import { getMonday, formatDateISO, addDays } from '../../utils/weekUtils';
import { createWeekCache } from '../../utils/weekCache';
import { PRIO_COLORS as prioColors, TYPE_COLORS as typeColors } from '../../utils/taakKleuren';
import { getCapacityColor, getCapacityBgColor, DAGSTAAT_STIJL } from '../../utils/capacityColors';
import { AFGEROND_SET } from '../../utils/taakStatus';
// ISO-weeknummer (maandag = eerste dag) voor de weekgrenzen bij 2/4 weken.
const isoWeekNr = (d) => {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dag = x.getUTCDay() || 7; x.setUTCDate(x.getUTCDate() + 4 - dag);
  const jaarStart = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil((((x - jaarStart) / 86400000) + 1) / 7);
};

// Constants
// Uren per werkdag en de per-type inschatting staan in de capaciteitsregels
// (instelbaar door de planner) — zie utils/capacityCalc.js.
const DAY_NAMES = ['Ma', 'Di', 'Wo', 'Do', 'Vr'];

// Kleuren komen uit de gedeelde kaart — zie utils/taakKleuren.js.

const GROUP_ORDER = ['Vrije dag', 'Vrij/intern', 'Draaidag', 'Afspraak', 'Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Vanallesuurtje', 'Intern'];

// Uren netjes afronden op 1 decimaal, zonder overbodige ",0" (6.3333 → 6.3, 8 → 8).
const fmtHours = (h) => {
  const r = Math.round((Number(h) || 0) * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

// getMonday / formatDateISO / getWeekNumber komen uit utils/weekUtils, zodat alle
// planningspagina's dezelfde week tonen voor dezelfde datum.
const formatDateDisplay = (date) => date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });



/** Process tasks for expanded view: group Vanallesuurtje, build priority groups */
function processTasks(tasks, days) {
  // Normalize: person-tasks-range uses 'status', week-overview used 'prio'
  const normalized = (tasks || []).map(t => ({ ...t, prio: t.prio || t.status || '' }));
  const filtered = normalized.filter(t => !AFGEROND_SET.has(t.prio));
  const seen = new Set();
  const unique = filtered.filter(t => { if (seen.has(t.id)) return false; seen.add(t.id); return true; });

  const processed = [];
  const vanallesuurtjeByDay = {};
  days.forEach(d => { vanallesuurtjeByDay[d.iso] = []; });

  unique.forEach(task => {
    const startISO = task.startDate?.split('T')[0];
    if (!startISO) return;
    if (task.prio === 'Vanallesuurtje') {
      if (vanallesuurtjeByDay[startISO] !== undefined) {
        vanallesuurtjeByDay[startISO].push(task);
      }
      return;
    }
    processed.push(task);
  });

  days.forEach(day => {
    const vTasks = vanallesuurtjeByDay[day.iso];
    if (vTasks.length > 0) {
      processed.push({
        id: `vanallesuurtje-${day.iso}`,
        name: `${vTasks.length} Vanallesuurtje${vTasks.length > 1 ? 's' : ''}`,
        prio: 'Vanallesuurtje', hours: vTasks.reduce((s, t) => s + (t.hours || 0), 0),
        startDate: day.iso, endDate: day.iso,
        isGrouped: true, groupedTasks: vTasks,
        ...(vTasks.length === 1 ? {
          project: vTasks[0].project,
          projectNumber: vTasks[0].projectNumber,
          client: vTasks[0].client,
          projectName: vTasks[0].projectName,
        } : { project: null }),
      });
    }
  });

  // Build priority groups
  const groupSet = new Set();
  processed.forEach(t => {
    const key = t.prio || t.type || 'Geen prio';
    groupSet.add(key);
  });
  const groups = [...groupSet].sort((a, b) => {
    const aI = GROUP_ORDER.indexOf(a); const bI = GROUP_ORDER.indexOf(b);
    if (aI === -1 && bI === -1) return a.localeCompare(b);
    if (aI === -1) return 1; if (bI === -1) return -1;
    return aI - bI;
  });

  return { processed, groups };
}

/** Get tasks for a day (for tooltip) */
// intern = alleen Full Frame schuift aan (verschuifbaar), extern = klant/derde
// erbij (beschermd — moeilijker omheen te plannen), taak = solo geblokte tijd.
const AFSPRAAK_SOORT = {
  extern: { label: 'extern', kleur: '#0ea5e9' },
  intern: { label: 'intern', kleur: '#a855f7' },
  taak: { label: 'geblokte werktijd', kleur: '#64748b' },
};

// Een draaidag of vrije dag ís de dag — geen "4 uur" maar de hele dag. In de
// uitklap tonen die rijen 'dag' per dag en tellen ze niet mee in het totaal
// (de capaciteitsrij sluit ze ook uit; anders spreken de cijfers elkaar tegen).
const isVolledagTaak = (t) => ['Draaidag', 'Vrije dag'].includes(t.type) || ['Draaidag', 'Vrije dag'].includes(t.prio || t.status || '');

function getTasksForDay(tasks, dayISO) {
  return (tasks || []).filter(t => {
    const prio = t.prio || t.status || '';
    if (AFGEROND_SET.has(prio)) return false;
    // Een gebundelde rij (meetings, vanallesuurtjes) hoort alleen bij de dagen
    // waarop er echt iets staat — niet bij alles tussen start en eind.
    if (t.hoursByDay) return Boolean(t.hoursByDay[dayISO]);
    const start = t.startDate?.split('T')[0];
    const end = t.endDate?.split('T')[0] || start;
    return start && dayISO >= start && dayISO <= end;
  });
}

// Effectieve uren zoals de berekening ze telt (UrenWerk, of per-type inschatting).
const effHours = (t, rules) => effectiveTaskHours(t, rules);
// Uren van een taak verdeeld over de werkdagen in zijn bereik (zelfde verdeling
// als de capaciteitsberekening) → { iso: uren } voor de zichtbare dagen.
function taskHoursPerDay(task, dayISOs, rules) {
  // Expliciete dag→uren map (bv. gebundelde vanallesuurtjes) heeft voorrang.
  if (task.hoursByDay) {
    const out = {};
    dayISOs.forEach(d => { if (task.hoursByDay[d]) out[d] = task.hoursByDay[d]; });
    return out;
  }
  const s = (task.startDate || '').split('T')[0];
  const e = (task.endDate || task.startDate || '').split('T')[0];
  const start = new Date(s), end = new Date(e || s);
  if (isNaN(start)) return {};
  const all = [];
  const cur = new Date(start);
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) all.push(formatDateISO(cur));
    cur.setDate(cur.getDate() + 1);
  }
  if (!all.length) return {};
  const perDay = effHours(task, rules).val / all.length;
  const out = {};
  dayISOs.forEach(d => { if (all.includes(d)) out[d] = perDay; });
  return out;
}

// ─── Expanded Row: taken per prioriteitsgroep, UITGELIJND onder de dagkolommen ───
// Zo zie je meteen welke taak op welke dag valt (i.p.v. één brede lijst).
function ExpandedTasks({ tasks, days, weekHours, variant = 'grid', rules = DEFAULT_CAPACITY_RULES, gridCols: gridColsProp }) {
  const urenPerDag = rules.hoursPerDay;
  const { processed, groups } = useMemo(() => processTasks(tasks, days), [tasks, days]);
  const dayISOs = days.map(d => d.iso);

  // Rijen per groep. Vanallesuurtjes → ÉÉN gebundelde rij (uren over de dagen),
  // i.p.v. één losse rij per dag.
  const rowsForGroup = (group) => {
    const gt = processed.filter(t => (t.prio || t.type || 'Geen prio') === group);
    if (group === 'Vanallesuurtje' && gt.length > 1) {
      const hoursByDay = {};
      const subtasks = [];
      gt.forEach(t => {
        const iso = (t.startDate || '').split('T')[0];
        hoursByDay[iso] = (hoursByDay[iso] || 0) + effHours(t, rules).val;
        if (t.groupedTasks) subtasks.push(...t.groupedTasks); else subtasks.push(t);
      });
      const total = Object.values(hoursByDay).reduce((s, h) => s + h, 0);
      return [{
        id: `vanalles-bundle-${group}`, name: `Vanallesuurtjes (${gt.length} dagen)`,
        prio: 'Vanallesuurtje', hours: total, hoursByDay,
        isGrouped: true, groupedTasks: subtasks,
      }];
    }
    // Zelfde project meerdere keren in de groep (deliverable-taken) → één
    // gebundelde rij per project met de uren per dag opgeteld; de losse taken
    // staan in de tooltip. Dertien keer "9646 · Brandwebbing…" afgekapt lezen
    // zegt minder dan één regel met een teller.
    const perProject = new Map();
    const los = [];
    gt.forEach(t => {
      const label = taskProjectLabel(t);
      if (!label) { los.push(t); return; }
      if (!perProject.has(label)) perProject.set(label, []);
      perProject.get(label).push(t);
    });
    const rows = [...los];
    perProject.forEach((ts) => {
      if (ts.length === 1) { rows.push(ts[0]); return; }
      const hoursByDay = {};
      ts.forEach(t => {
        Object.entries(taskHoursPerDay(t, dayISOs, rules)).forEach(([d, h]) => {
          hoursByDay[d] = (hoursByDay[d] || 0) + h;
        });
      });
      rows.push({
        id: `project-bundle-${group}-${ts[0].projectNumber || ts[0].project}`,
        name: `${ts.length} taken`,
        projectNumber: ts[0].projectNumber, client: ts[0].client,
        projectName: ts[0].projectName, project: ts[0].project,
        prio: ts[0].prio, type: ts[0].type,
        hours: ts.reduce((som, t) => som + effHours(t, rules).val, 0),
        hoursByDay, isGrouped: true, groupedTasks: ts,
        startDate: ts.map(t => t.startDate || '').sort()[0],
        endDate: ts.map(t => t.endDate || t.startDate || '').sort().slice(-1)[0],
      });
    });
    return rows.sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
  };
  const fmtRange = (t) => {
    const s = (t.startDate || '').split('T')[0];
    const e = (t.endDate || t.startDate || '').split('T')[0];
    const d = (iso) => { const x = new Date(iso); return isNaN(x) ? '' : x.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }); };
    return e && e !== s ? `${d(s)}–${d(e)}` : d(s);
  };

  if (!processed.length) {
    return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 3, py: 1 }}>Geen taken deze week.</Typography>;
  }

  // Eén dag agenda in een tooltip — gebruikt per dagvak van de meetings-rij.
  const agendaDagTooltip = (iso, evts) => (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
        {new Date(iso).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'short' })}
      </Typography>
      {evts.map((ev, i) => {
        const soort = AFSPRAAK_SOORT[ev.soort] || AFSPRAAK_SOORT.intern;
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: soort.kleur, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {ev.start_time}–{ev.end_time} {ev.subject} <span style={{ color: soort.kleur, fontWeight: 700 }}>({soort.label})</span>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  const taskTooltip = (task) => task.agendaEvents ? (
    <Box sx={{ p: 0.5, maxWidth: 280 }}>
      <Typography variant="caption" fontWeight={700} display="block">Meetings uit de agenda</Typography>
      <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
        {Object.values(task.agendaEvents).reduce((n, evts) => n + evts.length, 0)} afspraken deze week — hover op een dagvak voor de afspraken van die dag.
      </Typography>
    </Box>
  ) : (
    <Box sx={{ p: 0.5, maxWidth: 280 }}>
      <Typography variant="caption" fontWeight={700} display="block">{taskProjectLabel(task) || task.name}</Typography>
      {task.project && <Typography variant="caption" display="block">Project: {task.project}</Typography>}
      <Typography variant="caption" display="block">Taak: {task.name}</Typography>
      <Typography variant="caption" display="block">{task.prio || task.type || '—'}</Typography>
      <Typography variant="caption" display="block">Periode: {fmtRange(task) || '—'}</Typography>
      <Typography variant="caption" display="block">Uren: {effHours(task, rules).estimated ? `~${effHours(task, rules).val} (ingeschat)` : `${fmtHours(task.hours)}`}u</Typography>
      {task.deadline && <Typography variant="caption" display="block">Deadline: {String(task.deadline).split('T')[0]}</Typography>}
      {task.isGrouped && task.groupedTasks && task.groupedTasks.map((t, i) => (
        <Typography key={i} variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
          • {taskProjectLabel(t) ? `${taskProjectLabel(t)}: ` : ''}{t.name} {t.hours ? `(${fmtHours(t.hours)}u)` : ''}
        </Typography>
      ))}
    </Box>
  );

  // ── Compacte lijst (mobiel): één regel per taak met dagen erbij ──
  if (variant === 'list') {
    return (
      <Box sx={{ py: 1, px: 1.5 }}>
        {groups.map(group => {
          const gt = rowsForGroup(group);
          if (!gt.length) return null;
          const dot = prioColors[group] || typeColors[group] || '#9ca3af';
          return (
            <Box key={group} sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dot }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151' }}>{group}</Typography>
              </Stack>
              <Stack spacing={0.25} sx={{ pl: 2 }}>
                {gt.map((task) => {
                  const eh = effHours(task, rules);
                  return (
                    <Tooltip key={task.id} arrow placement="top" enterTouchDelay={0} title={taskTooltip(task)}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.15 }}>
                        <Typography variant="caption" sx={{ flex: 1, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {taskProjectLabel(task)
                          ? <><b>{taskProjectLabel(task)}</b><span style={{ color: '#94a3b8' }}> · {task.name}</span></>
                          : task.name}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">{fmtRange(task)}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: eh.estimated ? '#b45309' : '#475569', minWidth: 40, textAlign: 'right' }}>
                          {eh.estimated ? `~${fmtHours(eh.val)}` : fmtHours(eh.val)}u
                        </Typography>
                      </Stack>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
        {weekHours !== undefined && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, pt: 0.5, borderTop: '1px dashed #e2e8f0', fontWeight: 800, color: '#475569', textAlign: 'right' }}>
            Totaal {days.length > 5 ? 'deze periode' : 'deze week'}: {fmtHours(weekHours)}u van {days.length * urenPerDag}u ({Math.round((weekHours / (days.length * urenPerDag)) * 100)}%)
          </Typography>
        )}
      </Box>
    );
  }

  // ── Dag-grid (desktop): kolommen exact onder de dagen van de rij erboven ──
  // ÉÉN raster met de hoofdtabel (feedback 20 aug: de uitklap had eigen
  // kolombreedtes en de dagvakken lagen niet onder die van de hoofdrij).
  // De gedeelde definitie komt van de pagina; de fallback is alleen voor de
  // mobiele 'list'-variant, die geen grid tekent.
  const gridCols = gridColsProp || `220px repeat(${days.length}, minmax(100px, 1fr)) 70px`;
  return (
    <Box sx={{ py: 1 }}>
      {/* mini day-header binnen de uitklap voor houvast */}
      <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, pb: 0.5, borderBottom: '1px solid #eef2f6' }}>
        <Box />
        {days.map(d => (
          <Typography key={d.iso} variant="caption" sx={{ textAlign: 'center', color: '#94a3b8', fontWeight: 700, fontSize: '0.65rem' }}>
            {d.dayName}
          </Typography>
        ))}
        <Typography variant="caption" sx={{ textAlign: 'right', color: '#94a3b8', fontWeight: 700, fontSize: '0.65rem', pr: 0.5 }}>tot.</Typography>
      </Box>

      {groups.map(group => {
        const groupTasks = rowsForGroup(group);
        if (groupTasks.length === 0) return null;
        const dot = prioColors[group] || typeColors[group] || '#9ca3af';
        return (
          <Box key={group}>
            {/* Groep-header (volle breedte) */}
            <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, pt: 0.75 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ gridColumn: '1 / -1' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dot, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151' }}>{group}</Typography>
              </Stack>
            </Box>
            {groupTasks.map((task) => {
              const col = prioColors[task.prio] || typeColors[task.type] || '#94a3b8';
              const eh = effHours(task, rules);
              const perDay = taskHoursPerDay(task, dayISOs, rules);
              return (
                <Tooltip key={task.id} arrow placement="top" enterTouchDelay={0}
                  title={task.agendaEvents ? '' : taskTooltip(task)}>
                  <Box sx={{
                    display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center',
                    borderRadius: 0.5, cursor: 'default', '&:hover': { bgcolor: '#f1f5f9' },
                  }}>
                    {/* Naam-kolom — bij de meetings-rij hangt de samenvatting hiér,
                        zodat hij niet over de dag-tooltips heen stapelt */}
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, pr: 1 }}>
                      <Box sx={{ width: 3, height: 14, borderRadius: 1, bgcolor: col, flexShrink: 0 }} />
                      {task.agendaEvents ? (
                        <Tooltip arrow placement="top" enterTouchDelay={0} title={taskTooltip(task)}>
                          <Typography variant="caption" sx={{ color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                            {task.name}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {taskProjectLabel(task)
                            ? <><b>{taskProjectLabel(task)}</b><span style={{ color: '#94a3b8' }}> · {task.name}</span></>
                            : task.name}
                        </Typography>
                      )}
                    </Stack>
                    {/* Dag-kolommen — uren op de dag waar de taak valt. Bij de
                        meetings-rij hangt de tooltip per dagvak (alleen díe dag),
                        niet op de rij (dat werd één toren van een hele week). */}
                    {days.map(d => {
                      const h = perDay[d.iso];
                      if (h && isVolledagTaak(task)) {
                        return (
                          <Typography key={d.iso} variant="caption" sx={{
                            textAlign: 'center', fontWeight: 700, color: col, fontSize: '0.68rem',
                            textTransform: 'uppercase', letterSpacing: '0.04em',
                          }}>
                            dag
                          </Typography>
                        );
                      }
                      const evts = task.agendaEvents?.[d.iso];
                      // Externe afspraak die dag → het getal kleurt extern-blauw
                      // met een ◆: zonder hoveren zichtbaar dat de dag moeilijker
                      // in te plannen is.
                      const heeftExtern = Boolean(evts?.some(ev => ev.soort === 'extern'));
                      const cel = (
                        <Typography key={d.iso} variant="caption" sx={{
                          textAlign: 'center', fontWeight: h ? (heeftExtern ? 800 : 700) : 400,
                          color: h ? (heeftExtern ? '#0284c7' : (eh.estimated ? '#b45309' : '#334155')) : '#e2e8f0',
                          fontSize: '0.72rem',
                          cursor: task.agendaEvents && h ? 'help' : 'inherit',
                        }}>
                          {h ? `${eh.estimated ? '~' : ''}${fmtHours(h)}${heeftExtern ? ' ◆' : ''}` : '·'}
                        </Typography>
                      );
                      return evts?.length ? (
                        <Tooltip key={d.iso} arrow placement="top" enterTouchDelay={0}
                          title={agendaDagTooltip(d.iso, evts)}>
                          {cel}
                        </Tooltip>
                      ) : cel;
                    })}
                    {/* Totaal */}
                    <Typography variant="caption" sx={{ textAlign: 'right', pr: 0.5, fontWeight: 700, color: eh.estimated ? '#b45309' : '#475569' }}>
                      {isVolledagTaak(task)
                        ? `${Object.keys(perDay).length} d`
                        : `${eh.estimated ? '~' : ''}${fmtHours(eh.val)}`}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        );
      })}

      {/* Totaal-rij per dag — uitgelijnd, telt op naar het rij-percentage */}
      {weekHours !== undefined && (
        <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, mt: 0.5, pt: 0.5, borderTop: '1px dashed #cbd5e1' }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569' }}>Totaal geteld</Typography>
          {days.map(d => {
            const dayTotal = processed.reduce((s, t) => s + (isVolledagTaak(t) ? 0 : (taskHoursPerDay(t, dayISOs, rules)[d.iso] || 0)), 0);
            const pct = Math.round((dayTotal / urenPerDag) * 100);
            return (
              <Typography key={d.iso} variant="caption" sx={{ textAlign: 'center', fontWeight: 800, color: dayTotal ? getCapacityColor(pct) : '#cbd5e1', fontSize: '0.72rem' }}>
                {dayTotal ? fmtHours(dayTotal) : '·'}
              </Typography>
            );
          })}
          <Typography variant="caption" sx={{ textAlign: 'right', pr: 0.5, fontWeight: 800, color: '#475569' }}>
            {fmtHours(weekHours)}
          </Typography>
        </Box>
      )}
      <Typography variant="caption" sx={{ display: 'block', px: 1, mt: 0.25, color: '#94a3b8', fontSize: '0.65rem' }}>
        Uren staan op de dag(en) waarop de taak valt · ~ = ingeschat (geen uren geschreven) · {days.length * urenPerDag}u = volle week
      </Typography>
    </Box>
  );
}

// ─── Main Component ───
const NAAMKOLOM_KEY = 'ff.capaciteit.naamkolom';

export default function CapacityOverview() {
  useKlantAfkortingen(); // klant-afkortingen laden vóór de labels renderen
  // Breedte van de taaknaam-kolom. Blijft staan tussen sessies, want wie hem één
  // keer breed zet wil hem de volgende dag niet opnieuw slepen.
  const [naamBreedte, setNaamBreedte] = useState(() => {
    const bewaard = Number(localStorage.getItem(NAAMKOLOM_KEY));
    return Number.isFinite(bewaard) && bewaard >= 180 && bewaard <= 700 ? bewaard : 220;
  });
  const zetNaamBreedte = (px) => {
    const v = Math.min(700, Math.max(180, Math.round(px)));
    setNaamBreedte(v);
    try { localStorage.setItem(NAAMKOLOM_KEY, String(v)); } catch { /* private mode */ }
  };

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agendaFout, setAgendaFout] = useState(false);
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  // 1/2/4 weken naast elkaar (Raijmond, wiki 24 aug + Teams 4 sep: "zolang ik maar
  // verder kan kijken dan 1 week"). Zelfde schakelaar als in de Planner To-Do.
  const [weken, setWeken] = useState(() => { try { return Number(localStorage.getItem('capaciteit:weken')) || 1; } catch { return 1; } });
  const zetWeken = (n) => { setWeken(n); try { localStorage.setItem('capaciteit:weken', String(n)); } catch { /* privémodus */ } };
  const [data, setData] = useState({ assignees: [], tasksByAssignee: {} });
  const [calHoursByName, setCalHoursByName] = useState({});   // { naam: { date: meetingUren } }
  const { rules, reload: herlaadRegels } = useCapacityRules();  // capaciteitsregels (planner)
  const [regelsOpen, setRegelsOpen] = useState(false);
  const [personen, setPersonen] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [functieFilter, setFunctieFilter] = useState('Alles');
  // Meerdere personen uit verschillende disciplines onder elkaar zetten om een
  // gezamenlijk moment te vinden (feedback #51). Leeg = de functiefilter geldt.
  const [gekozenPersonen, setGekozenPersonen] = useState([]);
  const [showStagiairs, setShowStagiairs] = useState(false);
  // Capaciteit gaat over het vaste team; freelancers alleen op verzoek (tool-feedback #33).
  const [showFreelancers, setShowFreelancers] = useState(false);

  const days = useMemo(() => {
    const result = [];
    for (let i = 0; i < weken * 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      const dayOfWeek = i % 7;
      if (dayOfWeek >= 5) continue;
      result.push({
        date, iso: formatDateISO(date), dayOfWeek,
        dayName: DAY_NAMES[dayOfWeek], display: formatDateDisplay(date),
        dayNumber: date.getDate(), isWeekend: false,
        weekStart: dayOfWeek === 0, weekNr: isoWeekNr(date),
      });
    }
    return result;
  }, [weekStart, weken]);

  // Eén rasterdefinitie voor hoofdtabel ÉN uitklap: de dagvakken liggen dan
  // altijd exact onder elkaar, en de breedteknop verstelt beide tegelijk
  // (feedback 20 aug: verschillende kolombreedtes bij openklappen verwarden).
  const hoofdGrid = `${naamBreedte}px repeat(${days.length}, minmax(${weken === 1 ? 100 : weken === 2 ? 88 : 74}px, 1fr)) 70px`;

  // Personen zijn week-onafhankelijk: één keer ophalen en delen met de vooruitgeladen weken.
  const personsPromiseRef = useRef(null);
  const laadPersonen = useCallback(() => {
    if (!personsPromiseRef.current) {
      personsPromiseRef.current = authFetch(`${BACKEND_URL}/Api/planner-todo/persons`)
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then(d => d.persons || [])
        .catch(err => { personsPromiseRef.current = null; throw err; });
    }
    return personsPromiseRef.current;
  }, []);

  // Eén week ophalen (personen + taken + agenda-uren). Wordt ook gebruikt om de vorige/
  // volgende week alvast in de buffer te zetten — daarom géén setState hierbinnen.
  const laadWeek = useCallback(async (start, aantalWeken = 1) => {
    const persons = await laadPersonen();
    if (persons.length === 0) return { persons: [], assignees: [], tasksByAssignee: {}, calByName: {} };

    const endDate = addDays(start, aantalWeken * 7 - 3); // vrijdag van de laatste week
    const params = new URLSearchParams();
    params.append('startDate', formatDateISO(start));
    params.append('endDate', formatDateISO(endDate));
    persons.forEach(p => params.append('personIds[]', p.id));

    // Taken + agenda-uren PARALLEL ophalen (was sequentieel — merkbaar sneller).
    const emails = persons.map(p => p.email).filter(Boolean);
    const [tasksRes, calByEmail] = await Promise.all([
      authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params}`),
      emails.length
        ? authFetch(`${BACKEND_URL}/Api/planner-todo/calendar-hours`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ emails, startDate: formatDateISO(start), endDate: formatDateISO(endDate), includeEvents: true }),
          }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(d => ({ hours: d?.hours || {}, events: d?.events || {} }))
            .catch(() => ({ hours: {}, events: {}, fout: true })) // FE-06: niet stil 0 uur tonen
        : Promise.resolve({ hours: {}, events: {} }),
    ]);
    if (!tasksRes.ok) throw new Error(`HTTP ${tasksRes.status}`);
    const tasksByPersonId = await tasksRes.json();

    // Build data structure keyed by firstName (for display)
    const assignees = [];
    const tasksByAssignee = {};
    const calByName = {};
    const agendaByName = {};
    persons.forEach(p => {
      const name = p.firstName || p.name;
      if (!name) return;
      // Airtable-Afspraak eruit (meetings komen uit de agenda), rest behouden.
      const base = (tasksByPersonId[p.id] || []).filter(t => t.type !== 'Afspraak');
      const email = (p.email || '').toLowerCase();
      const cal = calByEmail.hours[email] || {};
      // Afspraken zelf, gegroepeerd per dag — voor de tooltips en de
      // extern-markering ("die dag is moeilijker in te plannen").
      const perDag = {};
      (calByEmail.events[email] || []).forEach(ev => {
        (perDag[ev.date] = perDag[ev.date] || []).push(ev);
      });
      // ÉÉN meetings-rij per persoon (was één rij per dag): de uren staan via
      // hoursByDay op de juiste dagen; de capaciteit rekent met calByName
      // (de util slaat type 'Afspraak' over → geen dubbel).
      const dagen = Object.keys(cal).sort();
      const totaal = Object.values(cal).reduce((sum, h) => sum + Number(h || 0), 0);
      // "Meetings (agenda)" zei niets over wat er die week staat, terwijl je juist op
      // de naam afweegt of je er overheen kunt plannen. We noemen de afspraken die de
      // meeste tijd kosten; de rest staat in de tooltip. Prive/gevoelige onderwerpen
      // zijn engine-zijde al afgeschermd, dus die lekken hier niet.
      const urenPerOnderwerp = {};
      Object.values(perDag).flat().forEach((ev) => {
        const t = (ev.subject || 'Afspraak').trim();
        urenPerOnderwerp[t] = (urenPerOnderwerp[t] || 0) + Number(ev.hours || 0);
      });
      const onderwerpen = Object.entries(urenPerOnderwerp).sort((a, b) => b[1] - a[1]).map(([t]) => t);
      const meetingNaam = onderwerpen.length === 0
        ? 'Meetings (agenda)'
        : onderwerpen.slice(0, 2).join(' \u00b7 ') + (onderwerpen.length > 2 ? ` +${onderwerpen.length - 2}` : '');

      const meetingTasks = dagen.length ? [{
        id: `cal-${p.id}`, name: meetingNaam, type: 'Afspraak', status: 'Afspraak',
        hours: totaal, hoursByDay: cal, agendaEvents: perDag,
        startDate: dagen[0], endDate: dagen[dagen.length - 1],
      }] : [];
      assignees.push(name);
      tasksByAssignee[name] = [...base, ...meetingTasks];
      calByName[name] = cal;
      agendaByName[name] = perDag;
    });

    return { persons, assignees: assignees.sort((a, b) => a.localeCompare(b)), tasksByAssignee, calByName, agendaByName, agendaFout: calByEmail.fout === true };
  }, [laadPersonen]);

  // Buffer: opgehaalde weken blijven kort staan en de buurweken worden vooruitgeladen,
  // zodat doorklikken niet elke keer op Airtable wacht (wiki-feedback).
  const cacheRef = useRef(createWeekCache());

  const fetchData = useCallback(async ({ ververs = false } = {}) => {
    if (ververs) {
      cacheRef.current.clear();
      personsPromiseRef.current = null;
    }
    const key = `${formatDateISO(weekStart)}:${weken}`;
    const uitBuffer = cacheRef.current.get(key);
    setError(null);
    setLoading(!uitBuffer); // uit de buffer → direct tonen, geen spinner

    try {
      const res = uitBuffer ?? await cacheRef.current.load(key, () => laadWeek(weekStart, weken));
      setPersonen(res.persons);
      setCalHoursByName(res.calByName);
      setAgendaFout(res.agendaFout === true);
      setData({ assignees: res.assignees, tasksByAssignee: res.tasksByAssignee });
    } catch (err) {
      setError(`Kon data niet laden: ${err.message}`);
    } finally {
      setLoading(false);
    }

    [-1, 1].forEach(richting => {
      const buur = addDays(getMonday(weekStart), richting * 7);
      cacheRef.current.prefetch(`${formatDateISO(buur)}:${weken}`, () => laadWeek(buur, weken));
    });
  }, [weekStart, weken, laadWeek]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Robust string conversion - handles arrays, JSON strings of arrays, null, objects
  const safeStr = (v) => {
    if (v == null) return '';
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'string') {
      if (v.startsWith('[')) {
        try {
          const arr = JSON.parse(v);
          if (Array.isArray(arr)) return arr.join(', ');
        } catch { /* fall through */ }
      }
      return v;
    }
    return String(v);
  };
  const safeLower = (v) => safeStr(v).toLowerCase();

  // Map person name → functie
  const personFuncties = useMemo(() => {
    const map = {};
    personen.forEach(p => {
      const name = p.firstName || p.name || '';
      map[name] = safeStr(p.function);
    });
    return map;
  }, [personen]);

  // Map person name → type (Medewerker / Freelancer / Stagiair). Airtable kan het veld
  // als array teruggeven (multipleSelects) — naar tekst, anders matcht geen enkel filter.
  const personTypes = useMemo(() => {
    const map = {};
    personen.forEach(p => {
      const name = p.firstName || p.name || '';
      map[name] = Array.isArray(p.type) ? p.type.join(', ') : String(p.type || '');
    });
    return map;
  }, [personen]);

  // Only show these functies as filter chips
  const allFuncties = ['Animator', 'Cameraman', 'Editor', 'Regisseur', 'Social Media Manager'];

  // Filtered assignees: apply functie filter + stagiair toggle
  const filteredAssignees = useMemo(() => {
    const filterLower = safeLower(functieFilter);
    if (gekozenPersonen.length) {
      // Expliciete keuze wint van functie- en stagiairfilter, in de gekozen volgorde.
      return gekozenPersonen.filter((name) => data.assignees.includes(name));
    }
    return data.assignees.filter(name => {
      // Stagiair- en freelancerfilter
      if (!showStagiairs && (personTypes[name] || '').includes('Stagiair')) return false;
      if (!showFreelancers && /Freelancer|Bedrijf/.test(personTypes[name] || '')) return false;
      // Functie filter
      if (functieFilter === 'Alles') return true;
      return safeLower(personFuncties[name]).includes(filterLower);
    });
  }, [data.assignees, functieFilter, personFuncties, personTypes, showStagiairs, showFreelancers, gekozenPersonen]);

  // Capacity per assignee — gedeelde util (identiek aan Planner To-Do)
  const dayISOs = useMemo(() => days.map(d => d.iso), [days]);
  // Uren én dagstaat (vrij / draaidag) in één keer — die twee hoorden altijd al bij
  // elkaar, maar de indicator-stipjes werden hier apart en nét iets anders berekend.
  const dagBelasting = useMemo(() => {
    const result = {};
    data.assignees.forEach(name => {
      result[name] = computeDayLoad(data.tasksByAssignee[name] || [], dayISOs, calHoursByName[name] || {}, rules);
    });
    return result;
  }, [data, dayISOs, calHoursByName, rules]);
  const capacityData = useMemo(() => {
    const result = {};
    Object.entries(dagBelasting).forEach(([name, v]) => { result[name] = v.uren; });
    return result;
  }, [dagBelasting]);

  // Team averages (only filtered)
  const teamAverages = useMemo(() => {
    const avgs = {};
    const count = filteredAssignees.length || 1;
    days.forEach(d => {
      let sum = 0;
      filteredAssignees.forEach(name => { sum += (capacityData[name]?.[d.iso] || 0); });
      avgs[d.iso] = Math.round((sum / count) * 10) / 10;
    });
    return avgs;
  }, [capacityData, filteredAssignees, days]);

  // Navigatie zit in <WeekSelector> (vorige/volgende + datumprikker).
  const todayISO = formatDateISO(new Date());

  const toggleExpand = (name) => setExpanded(prev => ({ ...prev, [name]: !prev[name] }));

  // Vrije dagen vallen buiten het weekgemiddelde: drie volle dagen en twee dagen vakantie
  // is 100%, niet 60%.
  const weekAvg = (name) => weekAveragePct(capacityData[name] || {}, dayISOs, rules.hoursPerDay, dagBelasting[name]?.staat || {});
  const teamWeekAvg = () => {
    const total = days.reduce((s, d) => s + (teamAverages[d.iso] || 0), 0);
    return Math.round((total / (days.length * rules.hoursPerDay)) * 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error" action={<Button onClick={fetchData}>Opnieuw</Button>}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      {/* Header */}
      <PageHeader
        icon="grid_view"
        title="Capaciteit"
        subtitle="Bezetting per medewerker per dag"
        rightActions={(
          <>
            <WeekSelector weekStart={weekStart} onChange={setWeekStart} />
            <ToggleButtonGroup size="small" exclusive value={weken} onChange={(_e, v) => v && zetWeken(v)}
              sx={{ bgcolor: '#fff', '& .MuiToggleButton-root': { textTransform: 'none', px: 1.1, py: 0.35, fontSize: 12, fontWeight: 700 } }}>
              <ToggleButton value={1}>1 week</ToggleButton>
              <ToggleButton value={2}>2 weken</ToggleButton>
              <ToggleButton value={4}>4 weken</ToggleButton>
            </ToggleButtonGroup>
            <Button
              size="small" startIcon={<HelpIcon />} onClick={() => setRegelsOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#526075' }}
            >
              Zo rekenen we dit uit
            </Button>
            <Tooltip title="Vernieuwen">
              <IconButton onClick={() => fetchData({ ververs: true })} size="small"><RefreshIcon /></IconButton>
            </Tooltip>
          </>
        )}
      />
      {/* Taken die al op Afgerond staan en daardoor onzichtbaar zijn (9 okt) — alleen als er zijn */}
      <AfgerondSignaal sx={{ mb: 1 }} />

      {agendaFout && (
        <Alert severity="warning" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={() => fetchData({ ververs: true })} sx={{ textTransform: 'none', fontWeight: 700 }}>Opnieuw</Button>}>
          Agenda-uren niet geladen — de bezetting kan te laag lijken (meetings tellen nu niet mee).
        </Alert>
      )}

      {/* Functie filter + stagiair toggle */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {allFuncties.length > 0 && (
          <>
            <Chip label="Alles" size="small"
              variant={functieFilter === 'Alles' ? 'filled' : 'outlined'}
              color={functieFilter === 'Alles' ? 'primary' : 'default'}
              onClick={() => setFunctieFilter('Alles')}
            />
            {allFuncties.map(f => (
              <Chip key={f} label={f} size="small"
                variant={functieFilter === f ? 'filled' : 'outlined'}
                color={functieFilter === f ? 'primary' : 'default'}
                onClick={() => setFunctieFilter(f)}
              />
            ))}
          </>
        )}
        <Autocomplete
          multiple size="small" options={data.assignees || []} value={gekozenPersonen}
          onChange={(_, v) => setGekozenPersonen(v)} disableCloseOnSelect limitTags={3}
          sx={{ minWidth: 300, ml: 1 }}
          slotProps={{ paper: { sx: { minWidth: 340 } } }}
          renderInput={(params) => <TextField {...params} label="Personen naast elkaar" placeholder={gekozenPersonen.length ? '' : 'Kies meerdere…'} />}
          // Naam op één regel, functies als één grijze regel eronder (afgekapt) — de oude
          // variant liet de functies om de naam heen wikkelen (Joris, 15 sep).
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option} style={{ ...props.style, alignItems: 'center', paddingTop: 4, paddingBottom: 4 }}>
              <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.25 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2 }}>{option}</Typography>
                {personFuncties[option] && (
                  <Typography noWrap sx={{ fontSize: '0.7rem', color: '#94a3b8', lineHeight: 1.2 }} title={personFuncties[option]}>
                    {personFuncties[option]}
                  </Typography>
                )}
              </Box>
            </li>
          )}
        />
        <Box sx={{ flex: 1 }} />
        {/* Breedte van de taaknaam-kolom in de uitklap — hier bij de tabel zelf,
            niet in de paginakop (feedback: "rare locatie"). */}
        <Tooltip title="Breedte van de taaknaam-kolom (in de uitklap per persoon)">
          <Stack direction="row" alignItems="center" spacing={0}
            sx={{ border: '1px solid #e2e8f0', borderRadius: 4, px: 0.5, mr: 0.5 }}>
            <IconButton size="small" onClick={() => zetNaamBreedte(naamBreedte - 60)}
              disabled={naamBreedte <= 220} aria-label="Taaknaam-kolom smaller" sx={{ p: 0.25 }}>
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, px: 0.25 }}>
              taaknaam-kolom
            </Typography>
            <IconButton size="small" onClick={() => zetNaamBreedte(naamBreedte + 60)}
              disabled={naamBreedte >= 700} aria-label="Taaknaam-kolom breder" sx={{ p: 0.25 }}>
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Tooltip>
        <Chip
          label={showStagiairs ? '🎓 Stagiairs zichtbaar' : '🎓 Stagiairs verborgen'}
          size="small"
          variant={showStagiairs ? 'filled' : 'outlined'}
          color={showStagiairs ? 'secondary' : 'default'}
          onClick={() => setShowStagiairs(!showStagiairs)}
          sx={{ cursor: 'pointer' }}
        />
        <Chip
          label={showFreelancers ? '🤝 Freelancers zichtbaar' : '🤝 Freelancers verborgen'}
          size="small"
          variant={showFreelancers ? 'filled' : 'outlined'}
          color={showFreelancers ? 'secondary' : 'default'}
          onClick={() => setShowFreelancers(!showFreelancers)}
          sx={{ cursor: 'pointer' }}
        />
      </Box>

      {/* Mobiele weergave: compacte kaart per persoon (snel scannen) */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {filteredAssignees.map(name => {
          const cap = capacityData[name] || {};
          const avg = weekAvg(name);
          const isExp = expanded[name];
          const tasks = data.tasksByAssignee[name] || [];
          return (
            <Paper key={name} variant="outlined" sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}>
              <Box onClick={() => toggleExpand(name)} sx={{ p: 1.25, cursor: 'pointer' }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  {isExp ? <ExpandIcon sx={{ fontSize: 18, color: '#6b7280' }} /> : <CollapseIcon sx={{ fontSize: 18, color: '#6b7280' }} />}
                  <Typography sx={{ fontWeight: 700, fontSize: 14, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Typography>
                  <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: getCapacityBgColor(avg), minWidth: 50, textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 13, color: getCapacityColor(avg) }}>{avg}%</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }}>
                  {days.map(d => {
                    const pct = Math.round(((cap[d.iso] || 0) / rules.hoursPerDay) * 100);
                    return (
                      <Box key={d.iso} sx={{ flex: 1, textAlign: 'center', py: 0.5, borderRadius: 0.75, bgcolor: getCapacityBgColor(pct) }}>
                        <Typography sx={{ fontSize: 9.5, color: '#64748b', display: 'block', lineHeight: 1.1 }}>{(d.dayName || '').slice(0, 2)}</Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: getCapacityColor(pct) }}>{pct}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
              <Collapse in={isExp}>
                <Box sx={{ borderTop: '1px solid #e2e8f0', bgcolor: '#fafbfc' }}>
                  <ExpandedTasks tasks={tasks} days={days} rules={rules} variant="list" weekHours={days.reduce((s, d) => s + (cap[d.iso] || 0), 0)} />
                </Box>
              </Collapse>
            </Paper>
          );
        })}
        {filteredAssignees.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>Geen medewerkers.</Typography>
        )}
      </Box>

      {/* Heatmap Grid — desktop/tablet (op mobiel verborgen; daar staat de kaart-lijst) */}
      <Paper sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden', overflowX: 'auto' }}>
        {/* Header row */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: hoofdGrid,
          backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0',
        }}>
          <Box sx={{ px: 2, py: 1.5, fontWeight: 700, fontSize: '0.85rem' }}>Medewerker</Box>
          {days.map(d => (
            <Box key={d.iso} sx={{
              textAlign: 'center', py: 1.5,
              backgroundColor: d.iso === todayISO ? '#eef2ff' : undefined,
              borderLeft: weken > 1 && d.weekStart ? '2px solid #cbd5e1' : undefined,
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                {d.dayName}{weken > 1 && d.weekStart ? <Box component="span" sx={{ ml: 0.5, fontSize: '0.65rem', color: '#6366f1', fontWeight: 800 }}>wk {d.weekNr}</Box> : null}
              </Typography>
              <Typography variant="caption" color="text.secondary">{d.display}</Typography>
            </Box>
          ))}
          <Box sx={{ textAlign: 'center', py: 1.5, fontWeight: 700, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Week</Box>
        </Box>

        {/* Person rows */}
        {filteredAssignees.map(name => {
          const cap = capacityData[name] || {};
          const avg = weekAvg(name);
          const isExpanded = expanded[name];
          const tasks = data.tasksByAssignee[name] || [];
          const functie = personFuncties[name] || '';
          const dagStaat = dagBelasting[name]?.staat || {};
          const extraUren = dagBelasting[name]?.extra || {};
          const agenda = data.agendaByName?.[name] || {};

          return (
            <Box key={name}>
              {/* Capacity row */}
              <Box
                onClick={() => toggleExpand(name)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: hoofdGrid,
                  cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                  '&:hover': { backgroundColor: '#fafbfc' },
                  transition: 'background-color 0.15s',
                }}
              >
                {/* Name cell */}
                <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isExpanded ? <ExpandIcon sx={{ fontSize: 18, color: '#6b7280' }} /> : <CollapseIcon sx={{ fontSize: 18, color: '#6b7280' }} />}
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}>{name}</Typography>
                </Box>

                {/* Day cells */}
                {days.map(d => {
                  const hours = cap[d.iso] || 0;
                  const pct = Math.round((hours / rules.hoursPerDay) * 100);
                  const dayTasks = getTasksForDay(tasks, d.iso);
                  // Externe afspraak die dag? Dan is er een klant/derde bij en is
                  // de dag moeilijker in te plannen — dat wil je zíen in de cel.
                  const externeAfspraken = (agenda[d.iso] || []).filter(ev => ev.soort === 'extern');
                  // Vrij of draaidag: geen percentage maar een toestand — er kan die dag
                  // niets bij. Stond hier eerst als los stipje in de hoek, wat je bij het
                  // scannen van twintig rijen simpelweg niet ziet.
                  const stijl = DAGSTAAT_STIJL[dagStaat[d.iso]];
                  const extra = extraUren[d.iso] || 0;

                  return (
                    <Tooltip key={d.iso} arrow placement="top" title={
                      stijl || dayTasks.length > 0 ? (
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                            {name} - {d.dayName} {d.display}
                          </Typography>
                          {stijl && (
                            <Typography variant="caption" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                              {stijl.titel}
                              {extra > 0 && ` — let op: er staat ook nog ${fmtHours(extra)}u ander werk op deze dag`}
                            </Typography>
                          )}
                          {externeAfspraken.map((ev, i) => (
                            <Typography key={`ext-${i}`} variant="caption" display="block" sx={{ fontSize: '0.7rem', color: '#7dd3fc', fontWeight: 700 }}>
                              ◆ Externe afspraak: {ev.start_time}–{ev.end_time} {ev.subject}
                            </Typography>
                          ))}
                          {dayTasks.map((t, i) => {
                            const c = prioColors[t.prio] || typeColors[t.type] || '#9ca3af';
                            // De uren van DEZE dag, niet het totaal van de taak. Een
                            // meerdaagse taak of de agenda-regel (die de hele periode
                            // beslaat) liet anders het totaal zien: Michel kreeg zo
                            // "128u" te zien op één woensdag, terwijl dat de agenda-uren
                            // van vier weken waren (Joris, 9 sep).
                            const opDezeDag = taskHoursPerDay(t, dayISOs, rules)[d.iso];
                            const urenTekst = opDezeDag
                              ? `(${fmtHours(opDezeDag)}u)`
                              : (t.hours ? `(${fmtHours(t.hours)}u totaal)` : '');
                            return (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: c, flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                  {taskProjectLabel(t)
                                    ? <><b>{taskProjectLabel(t)}</b>{` · ${t.name}`}</>
                                    : t.name} {urenTekst}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      ) : ''
                    }>
                      <Box sx={{
                        textAlign: 'center', py: 1,
                        // `background` en niet `backgroundColor`: een optie-draaidag
                        // krijgt een gestreepte vulling, en die is een gradient.
                        background: stijl ? stijl.bg
                          : (d.iso === todayISO && pct === 0 ? '#eef2ff' : getCapacityBgColor(pct)),
                        transition: 'background-color 0.2s',
                        position: 'relative',
                      }}>
                        {externeAfspraken.length > 0 && (
                          <Box sx={{ position: 'absolute', top: 3, right: 4, width: 7, height: 7,
                            borderRadius: '2px', transform: 'rotate(45deg)', bgcolor: '#0ea5e9' }} />
                        )}
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: stijl ? stijl.kleur : getCapacityColor(pct) }}>
                          {stijl ? stijl.tekst : `${pct}%`}
                          {extra > 0 && ' ⚠'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          {dagStaat[d.iso] === 'vrij' ? '—' : `${fmtHours(stijl ? rules.hoursPerDay : hours)}u`}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}

                {/* Week avg */}
                <Box sx={{ textAlign: 'center', py: 1, backgroundColor: getCapacityBgColor(avg), display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: getCapacityColor(avg) }}>{avg}%</Typography>
                </Box>
              </Box>

              {/* Expanded: task bars */}
              <Collapse in={isExpanded}>
                <Box sx={{
                  borderBottom: '1px solid #e2e8f0', backgroundColor: '#fafbfc',
                }}>
                  <ExpandedTasks tasks={tasks} days={days} rules={rules} gridCols={hoofdGrid} weekHours={days.reduce((s, d) => s + (cap[d.iso] || 0), 0)} />
                </Box>
              </Collapse>
            </Box>
          );
        })}

        {/* Team average row */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: hoofdGrid,
          backgroundColor: '#f1f5f9', borderTop: '2px solid #cbd5e1',
        }}>
          <Box sx={{ px: 2, py: 1.5, fontWeight: 700, fontSize: '0.85rem', color: 'text.secondary' }}>
            Team gemiddelde
          </Box>
          {days.map(d => {
            const hours = teamAverages[d.iso] || 0;
            const pct = Math.round((hours / rules.hoursPerDay) * 100);
            return (
              <Box key={d.iso} sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: getCapacityColor(pct) }}>{pct}%</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{fmtHours(hours)}u</Typography>
              </Box>
            );
          })}
          <Box sx={{ textAlign: 'center', py: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: getCapacityColor(teamWeekAvg()) }}>{teamWeekAvg()}%</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Uitleg + (voor de planner) de instelbare rekenregels */}
      <Dialog open={regelsOpen} onClose={() => setRegelsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Capaciteitsregels</DialogTitle>
        <DialogContent dividers>
          <CapaciteitsRegels onSaved={herlaadRegels} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegelsOpen(false)} sx={{ textTransform: 'none' }}>Sluiten</Button>
        </DialogActions>
      </Dialog>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Legenda:</Typography>
        {[
          { label: '0%', color: '#e5e7eb', bg: '#f9fafb' },
          { label: '< 50%', color: '#22c55e', bg: '#f0fdf4' },
          { label: '50-79%', color: '#eab308', bg: '#fefce8' },
          { label: '80-99%', color: '#f97316', bg: '#fff7ed' },
          { label: '100%+', color: '#ef4444', bg: '#fef2f2' },
        ].map(item => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, backgroundColor: item.bg }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: item.color }}>{item.label}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, backgroundColor: '#f1f5f9' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#64748b' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Vrij</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1, backgroundColor: '#fff7ed' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f97316' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#f97316' }}>Draaidag</Typography>
        </Box>
        {/* Twee derde van de draaidagen staat nog als optie in de agenda; die
            gestreepte cellen zijn vastgehouden maar niet bevestigd. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1,
          background: 'repeating-linear-gradient(135deg, #fff7ed, #fff7ed 6px, #ffedd5 6px, #ffedd5 12px)' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', border: '2px solid #f97316' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#c2410c' }}>Draaidag (optie)</Typography>
        </Box>
      </Box>
    </Box>
  );
}
